import {
  Heading,
  Spacer,
  Text,
  useMediaQuery,
  useMergedRefs,
} from '@sparemin/blockhead';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import EddyEmbed, { EddyElement, EddyEmbedProps } from 'blocks/EddyEmbed';
import { useClipSelect } from '../ClipSelectContext';
import { useClipSelectDetailCardContext } from './ClipSelectDetailCardContext';
import ClipSelectDetailCardTimecodes from './ClipSelectDetailCardTimecodes';
import ClipSelectDetailControls from './ClipSelectDetailControls';
import { DEFAULT_LOADING_CLIP_DETAIL_TRANSCRIPT_MESSAGE } from './constants';
import { editPanelBlock as block } from './utils';

interface ClipSelectDetailEditPanelProps
  extends Pick<EddyEmbedProps, 'onSeek' | 'onTranscriptUpdate'> {
  onPlaybackChange?: (isPlaying: boolean) => void;
  onReady?: () => void;
  onSubmit: () => void;
  onAdvancedEditor: () => void;
}

const ClipSelectDetailEditPanel = React.forwardRef<
  EddyElement,
  ClipSelectDetailEditPanelProps
>(
  (
    {
      onAdvancedEditor,
      onPlaybackChange,
      onSeek,
      onSubmit,
      onReady,
      onTranscriptUpdate,
    },
    forwardedRef,
  ) => {
    const { isPlaying, suggestionId: id } = useClipSelectDetailCardContext();
    const { suggestedClips, eddyProjectId } = useClipSelect();

    // See comment in handlePlay.
    //
    // When Eddy dispatches a play event, that means the audio in Eddy is already
    // playing.  For this detail card, playback of Eddy and the preview iframe is
    // controlled by the isPlaying state.
    //
    // In order to allow playback to be controlled, when we get a play message we
    // first pause the player, then call onPlaybackChange.  If the parent wants
    // playback to begin, it can do so by setting the isPlaying state in that handler.
    //
    // Pausing playback in the play handler, however, fires a pause event, which then
    // kicks off an infinite loop of pausing and playing, causing stuttering in the playback.
    //
    // this flag is a way to skip the pause handler and avoid the infinite loop
    const suppressPauseHandler = useRef(false);

    const eddyRef = useRef<EddyElement>();
    const suggestion = useMemo(
      () => suggestedClips.find(s => s.id === id) || ({} as any),
      [id, suggestedClips],
    );
    const {
      startMillis,
      endMillis,
      isLoadingTranscript,
      playable,
    } = suggestion;

    const isMobile = useMediaQuery(`@media (max-width: 1045px)`);

    const ref = useMergedRefs([forwardedRef, eddyRef]);

    const handlePlay = useCallback(() => {
      if (!isPlaying) {
        // Eddy will fire onPlay after its audio begins playing.  sometimes, we have
        // to delay playing the audio, like when the preview has to first be updated.
        // pausing here will stop playback - which will then begin in the effect below
        suppressPauseHandler.current = true;
        eddyRef.current?.pause();
        onPlaybackChange?.(true);
      }
    }, [onPlaybackChange, isPlaying]);

    const handlePause = useCallback(() => {
      if (suppressPauseHandler.current) {
        suppressPauseHandler.current = false;
      } else {
        onPlaybackChange?.(false);
      }
    }, [onPlaybackChange]);

    useEffect(() => {
      if (isPlaying) {
        eddyRef.current?.play();
      } else {
        eddyRef.current?.pause();
      }
    }, [isPlaying]);

    useEffect(() => {
      if (!eddyProjectId) {
        // The video preview relies on the onReady callback to load the video.
        // So even if no Eddy project is available (eddyProjectId is null), we
        // still call onReady to ensure the video loads.
        onReady?.();
      }
    }, [eddyProjectId, onReady]);

    return (
      <div className={block({ disabled: !playable })}>
        <div className={block('timecodes-row')}>
          <ClipSelectDetailCardTimecodes
            clipStartMillis={startMillis}
            clipEndMillis={endMillis}
          />
        </div>
        <div className={block('transcript-row')}>
          {isLoadingTranscript ? (
            <div>
              <Heading level={2} lineHeight={2} letterSpacing="normal">
                {DEFAULT_LOADING_CLIP_DETAIL_TRANSCRIPT_MESSAGE}
              </Heading>
            </div>
          ) : eddyProjectId ? (
            <EddyEmbed
              isAudioEnabled
              width="100%"
              height="100%"
              eddyProjectId={eddyProjectId}
              context="inline"
              margin={isMobile ? '0' : '0, 42, 18, 42'}
              startMillis={startMillis}
              endMillis={endMillis}
              className={block('eddy-embed')}
              onPause={handlePause}
              onPlay={handlePlay}
              onTranscriptUpdate={onTranscriptUpdate}
              onSeek={onSeek}
              onApplicationReady={onReady}
              ref={ref}
            />
          ) : (
            <Text variant="p" weight={400}>
              Transcript not available
            </Text>
          )}
        </div>
        <div className={block('footer-row')}>
          <div className={block('footer-fade')} />
          <Spacer
            className={block('footer-inner')}
            orientation="vertical"
            space="16px"
          >
            <ClipSelectDetailControls
              onAdvancedEditor={onAdvancedEditor}
              onSubmit={onSubmit}
            />
          </Spacer>
        </div>
      </div>
    );
  },
);

export default ClipSelectDetailEditPanel;
